"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { File } from "lucide-react";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";
import Dropzone, { type FileRejection } from "react-dropzone";
import { toast } from "sonner";
import { useAuth } from "@/components/providers/auth-context";
import { AnkiIcon } from "@/components/ui/icons";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";
import { useTRPC } from "@/trpc/client";
import { uploadFile } from "@/utils/supabase/resumeable-uploads";

const MAX_ANKI_FILE_SIZE = 100 * 1024 * 1024; // 100MB

type Props = {
  /** Pass an existing study set ID to import all cards into it (edit-page mode). */
  id?: string;
  onClose?: () => void;
  resetForm?: () => void;
  className?: string;
};

export const AnkiImportDropzone = ({
  id,
  onClose,
  resetForm,
  className,
}: Props) => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const router = useRouter();
  const posthog = usePostHog();
  const { userId } = useAuth();
  const t = useI18n();
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const fakeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startFakeProgress = useCallback(() => {
    // Slowly advance from current progress toward 95%
    fakeTimerRef.current = setInterval(() => {
      setImportProgress((prev) => {
        if (prev >= 95) {
          if (fakeTimerRef.current) clearInterval(fakeTimerRef.current);
          return 95;
        }
        // Ease off as we approach 95 — larger gap = bigger step
        const remaining = 95 - prev;
        const step = Math.max(0.3, remaining * 0.04);
        return Math.min(95, prev + step);
      });
    }, 300);
  }, []);

  const stopFakeProgress = useCallback(() => {
    if (fakeTimerRef.current) {
      clearInterval(fakeTimerRef.current);
      fakeTimerRef.current = null;
    }
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => stopFakeProgress();
  }, [stopFakeProgress]);

  const importFromAnkiMutation = useMutation(
    trpc.questions.importFromAnki.mutationOptions({
      onSuccess: (data) => {
        if (data.folderId) {
          toast.success(
            `Imported ${data.totalImported} cards into ${data.studySets.length} study sets`,
          );
          posthog.capture("anki_import_with_folder", {
            folderId: data.folderId,
            studySetCount: data.studySets.length,
            totalImported: data.totalImported,
          });
        } else {
          toast.success(
            `Successfully imported ${data.totalImported} cards from Anki`,
          );
          const studySet = data.studySets[0];
          if (studySet) {
            posthog.capture("study_set_created", {
              studySetId: studySet.id,
              creationMethod: "anki_import",
            });
          }
        }
        if (data.mediaSkipped) {
          toast.warning(
            "Images couldn't be extracted from this deck — it uses Anki's newer export format. To include images, re-export from Anki with \"Support older Anki versions\" checked.",
            { duration: 10000 },
          );
          posthog.capture("anki_import_media_skipped");
        }
        queryClient.invalidateQueries({
          queryKey: trpc.users.getPlanLimit.queryKey(),
        });
      },
      onError: (error) => {
        toast.error(
          error instanceof Error ? error.message : "Failed to import Anki file",
        );
      },
    }),
  );

  const handleImport = async (file: globalThis.File) => {
    setIsImporting(true);
    setImportProgress(10);

    if (!userId) {
      toast.error("You must be logged in to import Anki files.");
      setIsImporting(false);
      return;
    }

    const storagePath = `${userId}/${crypto.randomUUID()}-${file.name}`;

    try {
      // Upload binary file via resumable TUS upload (chunked, retryable)
      // Map upload progress to 0–50% of the overall bar
      await uploadFile("temp-anki-uploads", storagePath, file, (pct) => {
        setImportProgress(Math.round(pct * 0.5));
      });

      setImportProgress(50);
      startFakeProgress();

      // Call the tRPC mutation with the storage path instead of the file contents
      const result = await importFromAnkiMutation.mutateAsync({
        studySetId: id,
        storagePath,
        fileName: file.name,
      });

      stopFakeProgress();
      setImportProgress(100);

      if (id) {
        queryClient.invalidateQueries({
          queryKey: trpc.studysets.getById.queryKey({ id }),
        });
      } else if (result.folderId) {
        queryClient.invalidateQueries({
          queryKey: trpc.folders.getUserFolders.queryKey(),
        });
        router.push(`/folders/${result.folderId}`);
      } else if (result.studySets?.[0]) {
        router.push(`/studysets/${result.studySets[0].id}/edit#questions`);
      }

      onClose?.();
      resetForm?.();
    } catch (error) {
      stopFakeProgress();
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("Failed to import Anki file");
      }
      setImportProgress(0);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dropzone
      maxSize={MAX_ANKI_FILE_SIZE}
      accept={{
        "application/octet-stream": [".apkg"],
        "application/zip": [".apkg"],
      }}
      onError={(error) => toast.error(error.message)}
      multiple={false}
      onDropRejected={([reject]: FileRejection[]) => {
        if (reject?.errors.find(({ code }) => code === "file-too-large")) {
          toast.error("File exceeds maximum size of 100MB.");
        } else if (
          reject?.errors.find(({ code }) => code === "file-invalid-type")
        ) {
          toast.error("Please upload a valid Anki (.apkg) file.");
        } else {
          toast.error("Failed to upload file.");
        }
      }}
      onDrop={async (acceptedFiles) => {
        if (acceptedFiles.length === 0) {
          toast.error("Please upload a valid Anki (.apkg) file.");
          return;
        }

        const file = acceptedFiles[0]!;

        if (!file.name.endsWith(".apkg")) {
          toast.error("Please upload a valid Anki (.apkg) file.");
          return;
        }

        await handleImport(file);
      }}
    >
      {({ getRootProps, getInputProps, acceptedFiles }) => (
        <div
          onClick={(e) => e.stopPropagation()}
          {...getRootProps()}
          className={cn(
            "border border-dotted border-input rounded-lg",
            className,
          )}
        >
          <div
            className={cn(
              "flex flex-col items-center justify-center w-full h-full rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100",
              isImporting ? "pb-2" : "pb-4",
            )}
          >
            <div
              className={cn(
                "flex flex-col items-center justify-center",
                isImporting ? "pt-3 pb-2 gap-2" : "pt-5 pb-6 gap-4",
              )}
            >
              <div className="flex items-center justify-center">
                <AnkiIcon className="w-8 h-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="mb-2 text-sm text-foreground font-light">
                  <span className="font-medium">
                    {t("newStudySetDrawer.clickToUpload")}
                  </span>{" "}
                  {t("newStudySetDrawer.dragAndDrop")}
                </p>
                <p className="text-xs text-muted-foreground">
                  Supports Anki package (.apkg) files
                </p>
              </div>
              {acceptedFiles?.[0] ? (
                <div className="max-w-xs bg-white flex items-center rounded-md overflow-hidden outline outline-[1px] outline-zinc-200 divide-x divide-zinc-200">
                  <div className="px-3 py-2 h-full grid place-items-center">
                    <File className="h-4 w-4 text-blue-500" />
                  </div>
                  <div className="px-3 py-2 h-full text-sm truncate">
                    {acceptedFiles[0].name}
                  </div>
                </div>
              ) : null}
              {isImporting ? (
                <div className="w-full max-w-xs mx-auto">
                  <Progress
                    className="h-1 w-full bg-zinc-200"
                    value={importProgress}
                  />
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    Importing cards...
                  </p>
                </div>
              ) : null}
            </div>
            <input
              multiple={false}
              accept=".apkg"
              type="file"
              {...getInputProps()}
              id="anki-dropzone-file"
              className="hidden"
            />
          </div>
        </div>
      )}
    </Dropzone>
  );
};
